import { resetLayerSelection, selectLayer } from '../InteractiveMap/utils/projectLayers';

/**
 * Class Project Cards
 */
class ProjectCard {
  async init() {
    await this.handleCardListUpdates();
  }

  /**
   * Is an observer that check if project list register some changes on runtime and executes the project card list count
   * If a child has changed (e.g. its class has updated) or something, this function will trigger other methods they need to know about this changes
   *
   * @returns {Promise<void>}
   */
  async handleCardListUpdates() {
    const cardList = document.querySelector('#js-org-interactive-map-projects-list');

    const observer = await new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          this.updateCardLengthCount();
        } else if (mutation.type === 'attributes') {
          this.updateCardLengthCount();
        }
      }
    });

    observer.observe(cardList, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  /**
   * Checks on execution how many cards in the list are visible
   */
  updateCardLengthCount() {
    const resultsCountInfo = document.getElementById('js-org-interactive-map-count-results');
    const resultsNoCountInfo = document.getElementById('js-org-interactive-map-count-results-none');
    const cardItems = document.querySelectorAll('.mol--project-card.is-visible');

    if (cardItems.length === 0) {
      resultsNoCountInfo.style.display = 'inline-block';
      resultsCountInfo.style.display = 'none';
    }

    if (cardItems.length > 0) {
      resultsCountInfo.innerHTML = `${cardItems.length}`;
      resultsCountInfo.style.display = 'inline-block';
      resultsNoCountInfo.style.display = 'none';
    }
  }
}

export default ProjectCard;

/**
 * Handle the click and keydown event of a given single card element and switch active on outside click
 *
 * @param card - a card element
 * @param wrapper - element to check if the event target is outside or not
 * @param map - the map context (webMap) to find layers needs to be passed through
 * @param view - the view context for the extent needs to be passed through
 * @returns {Promise<void>}
 */
export async function handleCardEvents(card, wrapper, map, view) {
  const isActive = card.classList.toggle('is-active');

  if (isActive) {
    await selectLayer(card.dataset.id, map, view);
  } else {
    resetLayerSelection(map);
  }

  document.addEventListener('click', (event) => {
    if (!card.contains(event.target)) {
      card.classList.remove('is-active');
    }

    if (!wrapper.contains(event.target)) {
      resetLayerSelection(map);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === ' ') {
      if (!card.contains(event.target)) {
        card.classList.remove('is-active');
      }

      if (!wrapper.contains(event.target)) {
        resetLayerSelection(map);
      }
    }
  });
}

/**
 * Build all cards and its attributes, by checking given cms data with usage of html-templates
 *
 * @param map - map context passed through method
 * @param view - view context passed through method
 * @returns {Promise<void>}
 */
export async function buildCards(map, view) {
  const projectsMapWrapper = document.getElementsByClassName('org--interactive-map__projects-map-wrapper')[0];
  const cardsWrapper = document.getElementById('js-org-interactive-map-projects-list');
  const cardTemplate = document.getElementById('js-project-card-template');
  const cardAttributesTemplate = document.getElementById('js-project-card-attribute-template');
  const cardsCmsDataString = cardsWrapper.dataset.cardsCmsData;
  const cardsCmsData = cardsCmsDataString ? JSON.parse(cardsCmsDataString) : [];

  const cmsDataEntries = (cardsCmsData && cardsCmsData.length) ? cardsCmsData : [];
  const clones = [];

  for (const suitableAttributes of cmsDataEntries) {
    const rawName = suitableAttributes.title;

    /**
     * Is the processed collection from cms-data or demo json matched by given bookmark name (same like layer-id)
     *
     * includes: attributes[{key, label, value}], href, id, label, searchTerm and title
     */
    /* eslint-disable no-continue */
    if (!suitableAttributes) {
      continue;
    }

    // Build cards by html-templates
    const cardClone = cardTemplate.content.cloneNode(true);
    const card = cardClone.querySelector('.js-mol-project-card');
    const cardAttributesList = cardClone.querySelector('.js-mol-project-card-card-attribute-list');
    const title = cardClone.querySelector('.js-mol-project-card-title');
    const button = cardClone.querySelector('.js-mol-project-card-button');

    card.dataset.id = suitableAttributes.id || '';
    card.dataset.filterName = rawName;
    card.dataset.filterSearchTerm = (suitableAttributes.searchTerm && suitableAttributes.searchTerm.length) ? suitableAttributes.searchTerm : '';

    if (suitableAttributes.attributes && Array.isArray(suitableAttributes.attributes)) {
      title.innerText = suitableAttributes.title || rawName;

      suitableAttributes.attributes.forEach((attribute) => {
        const cardAttributesClone = cardAttributesTemplate.content.cloneNode(true);
        const attributeListElement = cardAttributesClone.querySelector('.js-mol-project-card-attribute');
        const attributeLabel = attributeListElement.querySelector('.js-mol-project-card-attribute-label');
        const attributeValue = attributeListElement.querySelector('.js-mol-project-card-attribute-value');

        attributeLabel.innerText = attribute.label || '';
        attributeValue.innerHTML = attribute.value || '';
        cardAttributesList.appendChild(cardAttributesClone);

        switch (true) {
          case attribute.key && attribute.key.toString() === 'type':
            card.dataset.filterProjectType = attribute.value;
            break;
          case attribute.key && attribute.key.toString() === 'status':
            card.dataset.filterProjectStatus = attribute.value;
            break;
          case attribute.key && attribute.key.toString() === 'start':
            card.dataset.filterProjectStart = attribute.value;
            break;
          case attribute.key && attribute.key.toString() === 'IBN':
            card.dataset.filterProjectEnd = attribute.value;
            break;
          default:
            break;
        }
      });

      const label = suitableAttributes.label ? suitableAttributes.label : `Details page ${rawName}`;
      if (button) {
        button.href = suitableAttributes.href || '#';
        button.setAttribute('aria-label', label);
      }
    } else {
      if (title) {
        title.innerText = suitableAttributes.title || rawName;
      }

      if (button) {
        button.href = suitableAttributes.href || '#';
        button.setAttribute('aria-label', suitableAttributes.label || `Details page ${rawName}`);
      }
    }

    card.classList.add('is-visible');
    card.addEventListener('click', () => handleCardEvents(card, projectsMapWrapper, map, view));
    card.addEventListener('keydown', async (event) => {
      if (event.key === ' ') {
        event.preventDefault();
        await handleCardEvents(card, projectsMapWrapper, map, view);
      }
    });

    const titleText = (title && title.textContent) ? title.textContent.trim() : rawName;
    clones.push({ fragment: cardClone, title: titleText });
  }

  // remove pre existing entries (optional, if create new)
  // while (cardsWrapper.firstChild) cardsWrapper.removeChild(cardsWrapper.firstChild);

  // Sort card list alphabetical by card title
  clones.sort((a, b) => a.title.localeCompare(b.title, 'de', { sensitivity: 'base' }));

  // Make sure list is empty
  cardsWrapper.innerHTML = '';

  for (const item of clones) {
    cardsWrapper.appendChild(item.fragment);
  }
}

/**
 * Updates the visibility of a card
 *
 * @param results - Iterator object like [{ id: (results.dataset.id / bookmark.name), name: '#could be empty', visible: (true / fals) }]
 */
export function updateVisibleCards(results = {}) {
  const cardsWrapper = document.getElementById('js-org-interactive-map-projects-list');
  const cards = cardsWrapper.querySelectorAll('.mol--project-card');

  cards.forEach((card) => {
    const id = card.dataset.id;
    const result = results.find(element => element.id === id);

    if (result && result.visible) {
      card.classList.add('is-visible');
    } else {
      card.classList.remove('is-visible');
    }
  });
}
